<script setup lang="ts">
import { TABLE_ROW_TYPE } from "./useTable";
import { useTableRow } from "./useTableRow";
import type { TableRowProps } from "./TableRowProps";

/**
 * **TableRow** (`e-table-row`)
 *
 * Renders a row within a `<e-table-head>` or `<e-table-body>`.
 * Supports row selection when the parent `<e-table>` has a `selectable` prop.
 *
 * Selection state is managed centrally by the `useTable` composable via the
 * shared stateStore — the component itself holds no local selection state.
 *
 * The `selected` attribute is synced to the host element so Light DOM CSS
 * selectors (e.g. `e-table-row[selected]::part(row)`) can style selected rows.
 *
 * @example
 * ```html
 * <!-- Basic row -->
 * <e-table-row>
 *   <e-table-cell>Cell 1</e-table-cell>
 *   <e-table-cell>Cell 2</e-table-cell>
 * </e-table-row>
 *
 * <!-- Pre-selected row (declarative) -->
 * <e-table-row selected>
 *   <e-table-cell>Cell 1</e-table-cell>
 * </e-table-row>
 *
 * <!-- Disabled row (click will not toggle selection) -->
 * <e-table-row disabled>
 *   <e-table-cell>Cell 1</e-table-cell>
 * </e-table-row>
 * ```
 */
defineOptions({
  name: TABLE_ROW_TYPE,
});

const props = withDefaults(defineProps<TableRowProps>(), {
  selected: false,
});

const { classes, isRowSelected, isRowSelectable, handleClick } = useTableRow({ props });
</script>

<style lang="css">
:host {
  display: contents;
}
</style>

<template>
  <div :class="classes.wrapper">
    <div
      part="row"
      :class="classes.base"
      :aria-selected="isRowSelectable ? isRowSelected : undefined"
      :role="isRowSelectable ? 'row' : undefined"
      @click="handleClick"
    >
      <slot />
    </div>
  </div>
</template>
